import { useState, useEffect, useCallback, useRef } from 'react';

const COMPANION_HTTP = 'http://127.0.0.1:21548';
const POLL_INTERVAL = 5000;

export interface CompanionFile {
  sha256: string;
  filename: string;
  path: string;
  size_bytes: number;
  mime_type: string;
  source?: string;
  // When set, the editor can fetch audio directly from this URL and skip
  // companion-app resolution. Used by the public library; always absent
  // for files the companion app itself enumerates.
  url?: string;
}

export interface CompanionStatus {
  available: boolean;
  folder: string | null;
  fileCount: number;
  files: CompanionFile[];
}

export function useCompanion() {
  const [status, setStatus] = useState<CompanionStatus>({
    available: false,
    folder: null,
    fileCount: 0,
    files: [],
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`${COMPANION_HTTP}/status`, { signal: AbortSignal.timeout(2000) });
      const data = await res.json();
      if (data.running) {
        const filesRes = await fetch(`${COMPANION_HTTP}/files`, { signal: AbortSignal.timeout(3000) });
        const files: CompanionFile[] = await filesRes.json();
        setStatus({ available: true, folder: data.folder, fileCount: data.file_count, files });
      } else {
        setStatus({ available: false, folder: null, fileCount: 0, files: [] });
      }
    } catch {
      setStatus({ available: false, folder: null, fileCount: 0, files: [] });
    }
  }, []);

  useEffect(() => {
    check();
    pollRef.current = setInterval(check, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [check]);

  const getFileUrl = useCallback((sha256: string): string | null => {
    if (!status.available) return null;
    const file = status.files.find(f => f.sha256 === sha256);
    if (!file) return null;
    return `${COMPANION_HTTP}/file/${sha256}`;
  }, [status]);

  return { ...status, getFileUrl, refresh: check };
}
